/**
 * SESSION
 *
 * One place that owns the signed-in session, because it used to live in five:
 * localStorage under "token", Vuex `state.token`, `axios.defaults.headers.common`,
 * a component-local `isMerchantUser` flag, and whatever each screen re-derived.
 *
 * What is stored:
 *   token           the ACCESS token. Same key as before, so every existing
 *                   `localStorage.getItem("token")` keeps working.
 *   amejpay.refresh the REFRESH token from auth/login. It was being discarded, which
 *                   is why a session could never be renewed and simply died.
 *   amejpay.role    "Merchant" or otherwise, captured at login. The header used to
 *                   re-fetch the profile to work this out, and the Dashboard link
 *                   raced that request.
 */

const ACCESS_KEY = 'token';
const REFRESH_KEY = 'amejpay.refresh';
const ROLE_KEY = 'amejpay.role';
const USER_KEY = 'amejpay.user';

/** A JWT is always far longer than this; anything shorter is a leftover or a blank. */
const MIN_TOKEN_LENGTH = 20;

function read(key) {
  try {
    const v = window.localStorage.getItem(key);
    return (v === null || v === undefined) ? '' : String(v);
  } catch (e) { return ''; }
}

function write(key, value) {
  try {
    if (value === null || value === undefined || value === '') {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, String(value));
    }
  } catch (e) { /* private mode - the session lives for this page only */ }
}

export function getAccessToken() {
  const t = read(ACCESS_KEY);
  return t.length >= MIN_TOKEN_LENGTH ? t : '';
}

export function getRefreshToken() {
  const t = read(REFRESH_KEY);
  return t.length >= MIN_TOKEN_LENGTH ? t : '';
}

export function isLoggedIn() {
  return Boolean(getAccessToken());
}

export function getRole() {
  return read(ROLE_KEY);
}

export function isMerchant() {
  return String(getRole()).toLowerCase() === 'merchant';
}

export function getUser() {
  try { return JSON.parse(read(USER_KEY) || '{}'); } catch (e) { return {}; }
}

/** Route name for whichever dashboard this account belongs on. */
export function dashboardRouteName() {
  return isMerchant() ? 'merchant-dashboard' : 'app';
}

/**
 * Stores whatever the auth response gave us. Tolerant of both shapes seen on this API:
 *   { tokens: { access, refresh }, user: { role } }   and   { access, refresh }
 */
export function saveSession(payload) {
  const data = payload || {};
  const tokens = data.tokens || data;
  const access = tokens.access || data.access || '';
  const refresh = tokens.refresh || data.refresh || '';
  const user = data.user || {};

  if (access) { write(ACCESS_KEY, access); }
  if (refresh) { write(REFRESH_KEY, refresh); }

  // The role can also arrive as author.merchant on the profile payload.
  let role = user.role || '';
  if (!role && user.merchant) { role = 'Merchant'; }
  if (role) { write(ROLE_KEY, role); }

  if (user && Object.keys(user).length) {
    write(USER_KEY, JSON.stringify({
      id: user.id, email: user.email, role: role || user.role || ''
    }));
  }
  return access;
}

/** Called after the profile loads, since `merchant` there is authoritative. */
export function rememberRoleFromProfile(profile) {
  const author = (profile && (profile.author || profile)) || {};
  if (author.merchant) { write(ROLE_KEY, 'Merchant'); }
  else if (author.merchant === null && getRole() === 'Merchant') {
    // The account is no longer a merchant - do not keep sending them to a
    // dashboard the API will refuse.
    write(ROLE_KEY, '');
  }
}

/** Replaces just the access token after a refresh. */
export function updateAccessToken(access) {
  if (access) { write(ACCESS_KEY, access); }
  return access;
}

export function clearSession() {
  write(ACCESS_KEY, '');
  write(REFRESH_KEY, '');
  write(ROLE_KEY, '');
  write(USER_KEY, '');
  try { window.sessionStorage.removeItem(ACCESS_KEY); } catch (e) { /* noop */ }
}

export default {
  getAccessToken, getRefreshToken, isLoggedIn, getRole, isMerchant, getUser,
  dashboardRouteName, saveSession, rememberRoleFromProfile, updateAccessToken, clearSession
};
